'use client';

import React, { useState, useEffect, useRef } from 'react';

import { PlayIcon } from '@depop/web-ui-kit/Icons/PlayIcon';
import { VolumeOffIcon } from '@depop/web-ui-kit/Icons/VolumeOffIcon';
import { VolumeOnIcon } from '@depop/web-ui-kit/Icons/VolumeOnIcon';
import { clsx } from 'clsx';

import styles from './styles.module.css';

import { Spinner } from '@/components/Spinner';
import { getLargestProductPictureFormat } from '@/modules/pictures/helpers/getLargestProductPictureFormat';
import { hasThumbnailFormats } from '@/modules/product/helpers/toProductMedia';
import { type ProductVideo } from '@/modules/product/types.ts';

type Props = {
  video: ProductVideo;
};

export function Video({ video }: Props) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  function handlePause() {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }

  function handlePlay() {
    setIsPlaying(true);
    // Some browsers throw an Unhandled Promise Rejection: AbortError: The operation was aborted." when there
    // are network issues. Here we catch that error and suppress it. Some older browsers do not return a Promise
    // from HTMLMediaElement.play so we check that first.
    const videoPlayPromise = videoRef.current && videoRef.current.play();
    if (videoPlayPromise) {
      videoPlayPromise.catch(() => null);
    }
  }

  function toggleAudio() {
    setIsMuted((isMuted) => !isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  }

  useEffect(() => {
    handlePause();
  }, []);

  return (
    <div className={styles.videoContainer}>
      <div className={styles.videoBackground}>
        {isLoading && (
          <Spinner className={styles.videoSpinner} color="scarlet" />
        )}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          playsInline
          // eslint-disable-next-line no-restricted-syntax
          data-testid="video"
          preload="metadata"
          ref={videoRef}
          poster={
            hasThumbnailFormats(video)
              ? getLargestProductPictureFormat(video.thumbnail.formats).url
              : undefined
          }
          onPlaying={() => setIsLoading(false)}
          onWaiting={() => setIsLoading(true)}
          onEnded={() => setIsPlaying(false)}
        >
          {video.outputs[1]?.url && (
            <source src={video.outputs[1].url} type="video/mp4" />
          )}
          {video.outputs[0]?.url && (
            <source src={video.outputs[0].url} type="application/x-mpegURL" />
          )}
        </video>

        <button
          className={clsx(styles.playButtonBase, styles.videoPlayButton)}
          // eslint-disable-next-line no-restricted-syntax
          data-testid="button__play"
          onClick={isPlaying ? handlePause : handlePlay}
        >
          {!isPlaying && (
            // eslint-disable-next-line no-restricted-syntax
            <PlayIcon data-testid="icon__play" color="white" size={96} />
          )}
        </button>

        {!isMuted && (
          <button
            className={clsx(styles.playButtonBase, styles.audioPlayButton)}
            // eslint-disable-next-line no-restricted-syntax
            data-testid="button__soundOff"
            onClick={toggleAudio}
          >
            <VolumeOnIcon size={40} />
          </button>
        )}
        {isMuted && (
          <button
            className={clsx(styles.playButtonBase, styles.audioPlayButton)}
            // eslint-disable-next-line no-restricted-syntax
            data-testid="button__soundOn"
            onClick={toggleAudio}
          >
            <VolumeOffIcon size={40} />
          </button>
        )}
      </div>
    </div>
  );
}
